import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styles from './WoodChoppingMinigame.module.css';

const CHOPPING_PHASES = {
  IDLE: 'idle',
  STARTING: 'starting',
  ACTIVE: 'active',
  RESULT: 'result',
};

const TREE_TYPES = {
  NORMAL: {
    id: 'normal',
    name: '🌲 Pine Tree',
    health: 3,
    color: '#4caf50',
    baseReward: 1,
    special: null,
    description: 'Standard tree - steady rhythm chopping'
  },
  HARDWOOD: {
    id: 'hardwood',
    name: '🌳 Oak Tree', 
    health: 5,
    color: '#8d6e63',
    baseReward: 2,
    special: 'tough',
    description: 'Tough tree - requires precise timing'
  },
  MAGICAL: {
    id: 'magical',
    name: '✨ Enchanted Tree',
    health: 4,
    color: '#9c27b0',
    baseReward: 3,
    special: 'magic',
    description: 'Magical tree - gives bonus wood but zones move!'
  },
  CRYSTAL: {
    id: 'crystal',
    name: '💎 Crystal Tree',
    health: 6,
    color: '#00bcd4',
    baseReward: 4,
    special: 'crystal',
    description: 'Rare tree - shrinking zones but huge rewards!'
  }
};

const POWER_UPS = {
  SHARP_AXE: {
    id: 'sharp',
    name: '🪓 Sharp Axe',
    description: '+1 damage per hit',
    duration: 10000
  },
  TIME_SLOW: {
    id: 'slow',
    name: '⏱️ Time Slow',
    description: 'Slower slider movement',
    duration: 8000
  },
  COMBO_BOOST: {
    id: 'combo',
    name: '🔥 Combo Master',
    description: 'Double combo multiplier',
    duration: 12000
  }
};

const WoodChoppingMinigame = ({ playerWoodcuttingSkill, onGameEnd }) => {
  const [phase, setPhase] = useState(CHOPPING_PHASES.IDLE);
  const [message, setMessage] = useState('');
  const [currentTree, setCurrentTree] = useState(null);
  const [treeHealth, setTreeHealth] = useState(0);
  const [totalWood, setTotalWood] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [treesChopped, setTreesChopped] = useState(0);
  const [activePowerUps, setActivePowerUps] = useState([]);
  
  // Slider mechanics
  const [sliderPosition, setSliderPosition] = useState(50);
  const [sliderDirection, setSliderDirection] = useState(1);
  const [targetZones, setTargetZones] = useState([]);
  const [lastHitTime, setLastHitTime] = useState(0);
  const [choppingAnimation, setChoppingAnimation] = useState(false);
  const [powerUpSpawn, setPowerUpSpawn] = useState(null);
  
  const animationFrameRef = useRef(null);
  const phaseRef = useRef(CHOPPING_PHASES.IDLE);
  const sessionIdRef = useRef(null);
  const sliderDirectionRef = useRef(1);
  const sliderPositionRef = useRef(50);
  const gameTimeRef = useRef(0);
  const powerUpTimeoutsRef = useRef([]);

  // Game settings with stable reference using useMemo
  const gameSettings = useMemo(() => {
    const baseSpeed = 0.15;
    const difficultyMultiplier = Math.min(1 + (treesChopped * 0.1), 2);
    const hasTimeSlow = activePowerUps.includes('slow');
    
    return {
      sliderSpeed: baseSpeed * difficultyMultiplier * (hasTimeSlow ? 0.5 : 1),
      targetZoneSize: Math.max(40 - (treesChopped * 2), 20),
      gameTimeout: 90000, // 1.5 minutes
      maxTrees: 8,
    };
  }, [treesChopped, activePowerUps]);

  // Update refs
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { sliderDirectionRef.current = sliderDirection; }, [sliderDirection]);
  useEffect(() => { sliderPositionRef.current = sliderPosition; }, [sliderPosition]);

  const resetGameState = useCallback(() => {
    setPhase(CHOPPING_PHASES.IDLE);
    setMessage('');
    setCurrentTree(null);
    setTreeHealth(0);
    setTotalWood(0);
    setCombo(0);
    setMaxCombo(0);
    setTreesChopped(0);
    setActivePowerUps([]);
    setSliderPosition(50);
    setSliderDirection(1);
    setTargetZones([]);
    setLastHitTime(0);
    setChoppingAnimation(false);
    setPowerUpSpawn(null);
    
    sliderPositionRef.current = 50;
    sliderDirectionRef.current = 1;
    gameTimeRef.current = 0;
    
    // Clear power-up timeouts
    powerUpTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    powerUpTimeoutsRef.current = [];
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Generate target zones based on tree type
  const generateTargetZones = useCallback((treeType) => {
    let numZones = 2;
    let zoneSize = gameSettings.targetZoneSize;
    
    if (treeType?.special === 'tough') {
      zoneSize = Math.max(zoneSize * 0.7, 15); // Smaller zones for tough trees
    } else if (treeType?.special === 'crystal') {
      zoneSize = Math.max(zoneSize * 0.5, 10); // Very small zones for crystal trees
      numZones = 1; // But only one zone
    }
    
    const zones = [];
    
    for (let i = 0; i < numZones; i++) {
      let finalPosition = Math.random() * (100 - zoneSize);
      let attempts = 0;
      
      // Keep trying until we find a non-overlapping position or hit max attempts
      while (attempts < 20) {
        let hasOverlap = false;
        
        // Check for overlap with existing zones
        for (let j = 0; j < zones.length; j++) {
          if (Math.abs(zones[j].position - finalPosition) < zoneSize + 5) {
            hasOverlap = true;
            break;
          }
        }
        
        if (!hasOverlap) {
          break; // Found a good position
        }
        
        finalPosition = Math.random() * (100 - zoneSize);
        attempts++;
      }
      
      zones.push({
        id: i,
        position: finalPosition,
        size: zoneSize,
        color: treeType?.color || '#4caf50',
        moving: treeType?.special === 'magic'
      });
    }
    
    return zones.sort((a, b) => a.position - b.position);
  }, [gameSettings.targetZoneSize]);

  // Spawn a new tree
  const spawnNewTree = useCallback(() => {
    const treeTypeKeys = Object.keys(TREE_TYPES);
    const weights = [50, 30, 15, 5]; // Normal, Hardwood, Magical, Crystal
    const random = Math.random() * 100;
    
    let selectedType = TREE_TYPES.NORMAL;
    let cumulativeWeight = 0;
    
    for (let i = 0; i < treeTypeKeys.length; i++) {
      cumulativeWeight += weights[i];
      if (random <= cumulativeWeight) {
        selectedType = TREE_TYPES[treeTypeKeys[i]];
        break;
      }
    }
    
    setCurrentTree(selectedType);
    setTreeHealth(selectedType.health);
    setTargetZones(generateTargetZones(selectedType));
    setMessage(`🌲 A ${selectedType.name} appears! ${selectedType.description}`);
  }, [generateTargetZones]);

  // Check for power-up spawn
  const checkPowerUpSpawn = useCallback(() => {
    if (Math.random() < 0.15 && !powerUpSpawn) { // 15% chance
      const powerUpKeys = Object.keys(POWER_UPS);
      const randomPowerUp = POWER_UPS[powerUpKeys[Math.floor(Math.random() * powerUpKeys.length)]];
      
      setPowerUpSpawn({
        ...randomPowerUp,
        position: Math.random() * 80 + 10, // 10-90% position
        timeout: setTimeout(() => {
          setPowerUpSpawn(null);
        }, 5000)
      });
    }
  }, [powerUpSpawn]);

  // Activate power-up
  const activatePowerUp = useCallback((powerUp) => {
    setActivePowerUps(prev => {
      // Don't add duplicate power-ups
      if (prev.includes(powerUp.id)) {
        return prev;
      }
      return [...prev, powerUp.id];
    });
    setMessage(`🎉 ${powerUp.name} activated! ${powerUp.description}`);
    
    const timeout = setTimeout(() => {
      setActivePowerUps(prev => prev.filter(id => id !== powerUp.id));
      setMessage('Power-up expired!');
    }, powerUp.duration);
    
    powerUpTimeoutsRef.current.push(timeout);
    
    // Clear the spawn
    if (powerUpSpawn?.timeout) {
      clearTimeout(powerUpSpawn.timeout);
    }
    setPowerUpSpawn(null);
  }, [powerUpSpawn]);

  // Animation loop for slider movement - stable version
  const animateSlider = useCallback((frame) => {
    if (phaseRef.current !== CHOPPING_PHASES.ACTIVE) {
      return;
    }

    // Calculate dynamic speed to avoid stale closure issues
    const baseSpeed = 0.15;
    const currentTreesChopped = treesChopped;
    const currentActivePowerUps = activePowerUps;
    const difficultyMultiplier = Math.min(1 + (currentTreesChopped * 0.1), 2);
    const hasTimeSlow = currentActivePowerUps.includes('slow');
    const sliderSpeed = baseSpeed * difficultyMultiplier * (hasTimeSlow ? 0.5 : 1);

    setSliderPosition(currentPos => {
      const currentDir = sliderDirectionRef.current;
      let newPosition = currentPos + sliderSpeed * currentDir;
      
      // Bounce off walls
      if (newPosition <= 0) {
        newPosition = 0;
        sliderDirectionRef.current = 1;
        setSliderDirection(1);
      } else if (newPosition >= 100) {
        newPosition = 100;
        sliderDirectionRef.current = -1;
        setSliderDirection(-1);
      }
      
      sliderPositionRef.current = newPosition;
      return newPosition;
    });

    // Handle magical tree zone movement
    if (currentTree?.special === 'magic') {
      setTargetZones(zones => zones.map(zone => ({
        ...zone,
        position: Math.max(0, Math.min(100 - zone.size, 
          zone.position + (Math.sin(Date.now() * 0.002) * 0.1)
        ))
      })));
    }

    gameTimeRef.current += 16;
    
    // Check for power-up spawn periodically
    if (gameTimeRef.current % 2000 < 16) { // Every 2 seconds
      checkPowerUpSpawn();
    }

    // Continue animation
    animationFrameRef.current = requestAnimationFrame(animateSlider);
  }, [treesChopped, activePowerUps, currentTree?.special, checkPowerUpSpawn]);

  // Force animation restart when needed
  useEffect(() => {
    if (phase === CHOPPING_PHASES.ACTIVE) {
      // Cancel any existing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Start fresh animation
      const startFresh = () => {
        if (phaseRef.current === CHOPPING_PHASES.ACTIVE) {
          animationFrameRef.current = requestAnimationFrame(animateSlider);
        }
      };
      
      // Small delay to ensure state is settled
      setTimeout(startFresh, 100);
    }
  }, [phase, animateSlider]);

  // Check if slider is in target zone
  const isInTargetZone = useCallback((position) => {
    return targetZones.some(zone => 
      position >= zone.position && position <= zone.position + zone.size
    );
  }, [targetZones]);

  // Check if slider hits power-up
  const isInPowerUp = useCallback((position) => {
    if (!powerUpSpawn) return false;
    return position >= powerUpSpawn.position && position <= powerUpSpawn.position + 8;
  }, [powerUpSpawn]);

  // Handle player click/chop
  const handleChop = useCallback(() => {
    if (phaseRef.current !== CHOPPING_PHASES.ACTIVE || !currentTree) return;
    
    const currentPosition = sliderPosition;
    const now = Date.now();
    const inTarget = isInTargetZone(currentPosition);
    const inPowerUp = isInPowerUp(currentPosition);
    
    // Activate power-up if hit
    if (inPowerUp && powerUpSpawn) {
      activatePowerUp(powerUpSpawn);
      return;
    }
    
    // Trigger chopping animation
    setChoppingAnimation(true);
    setTimeout(() => setChoppingAnimation(false), 200);
    
    if (inTarget) {
      // Calculate damage
      let damage = 1;
      if (activePowerUps.includes('sharp')) damage += 1;
      
      // Update combo
      const timeSinceLastHit = now - lastHitTime;
      const comboWindow = 2000; // 2 seconds to maintain combo
      
      let newCombo = combo;
      if (timeSinceLastHit < comboWindow) {
        newCombo = combo + 1;
      } else {
        newCombo = 1;
      }
      setCombo(newCombo);
      setMaxCombo(prev => Math.max(prev, newCombo));
      setLastHitTime(now);
      
      // Apply combo multiplier
      let comboMultiplier = Math.floor(newCombo / 3) + 1; // Every 3 hits increases multiplier
      if (activePowerUps.includes('combo')) comboMultiplier *= 2;
      
      const finalDamage = damage * comboMultiplier;
      
      setTreeHealth(prevHealth => {
        const newHealth = prevHealth - finalDamage;
        
        if (newHealth <= 0) {
          // Tree chopped down!
          const woodReward = currentTree.baseReward * comboMultiplier;
          setTotalWood(prev => prev + woodReward);
          setTreesChopped(prev => prev + 1);
          setMessage(`🎉 ${currentTree.name} chopped! +${woodReward} wood! Combo: ${newCombo}x`);
          
          // Check if game should continue
          if (treesChopped + 1 >= gameSettings.maxTrees) {
            setTimeout(() => {
              onGameEnd({ 
                outcome: 'success', 
                woodGathered: totalWood + woodReward, 
                treesChopped: treesChopped + 1,
                maxCombo: Math.max(maxCombo, newCombo),
                sessionId: sessionIdRef.current 
              });
            }, 1500);
            setPhase(CHOPPING_PHASES.RESULT);
          } else {
            // Spawn new tree
            setTimeout(() => {
              spawnNewTree();
            }, 1000);
          }
        } else {
          setMessage(`💪 Great hit! ${finalDamage} damage! Combo: ${newCombo}x (${newHealth}/${currentTree.health} HP left)`);
          // Regenerate zones for variety
          setTargetZones(generateTargetZones(currentTree));
        }
        
        return newHealth;
      });
    } else {
      // Miss - break combo
      setCombo(0);
      setMessage(`❌ Missed! Combo broken! Aim for the ${currentTree.name}'s weak spots!`);
    }
  }, [sliderPosition, isInTargetZone, isInPowerUp, powerUpSpawn, activatePowerUp, currentTree, combo, lastHitTime, maxCombo, activePowerUps, treesChopped, totalWood, gameSettings.maxTrees, onGameEnd, generateTargetZones, spawnNewTree]);

  // Start the game
  const startChopping = useCallback(() => {
    resetGameState();
    setPhase(CHOPPING_PHASES.STARTING);
    setMessage('🪓 Welcome to the Enhanced Wood Chopping Challenge! Chop different trees, build combos, and collect power-ups!');
    
    sessionIdRef.current = `woodchopping_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setTimeout(() => {
      setPhase(CHOPPING_PHASES.ACTIVE);
      spawnNewTree();
      
      // Start animation using the dedicated function
      // startAnimation(); // This line is removed as per the new_code, as the animation is now managed by useEffect
      
      // Game timeout
      setTimeout(() => {
        if (phaseRef.current === CHOPPING_PHASES.ACTIVE) {
          setMessage('Time\'s up! Let\'s see your final score...');
          setTimeout(() => {
            onGameEnd({ 
              outcome: 'success', 
              woodGathered: totalWood, 
              treesChopped: treesChopped,
              maxCombo: maxCombo,
              sessionId: sessionIdRef.current 
            });
          }, 1000);
          setPhase(CHOPPING_PHASES.RESULT);
        }
      }, gameSettings.gameTimeout);
      
    }, 3000);
  }, [resetGameState, spawnNewTree, gameSettings.gameTimeout, onGameEnd, totalWood, treesChopped, maxCombo]);

  // Single useEffect for starting the game
  useEffect(() => {
    startChopping();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      powerUpTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const renderGameArea = () => {
    if (phase === CHOPPING_PHASES.ACTIVE && currentTree) {
      const healthPercentage = (treeHealth / currentTree.health) * 100;
      
      return (
        <div className={styles.choppingArea}>
          {/* Tree Info */}
          <div className={styles.treeInfo}>
            <div className={styles.treeName}>{currentTree.name}</div>
            <div className={styles.healthBar}>
              <div 
                className={styles.healthFill} 
                style={{ 
                  width: `${healthPercentage}%`, 
                  backgroundColor: currentTree.color 
                }}
              />
              <span className={styles.healthText}>{treeHealth}/{currentTree.health}</span>
            </div>
          </div>

          {/* Stats */}
          <div className={styles.statsInfo}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>🪵 Wood:</span>
              <span className={styles.statValue}>{totalWood}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>🔥 Combo:</span>
              <span className={styles.statValue} style={{ color: combo > 0 ? '#ff9800' : '#666' }}>
                {combo}x
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>🌲 Trees:</span>
              <span className={styles.statValue}>{treesChopped}/{gameSettings.maxTrees}</span>
            </div>
          </div>

          {/* Power-ups */}
          {activePowerUps.length > 0 && (
            <div className={styles.activePowerUps}>
              {activePowerUps.map((powerUpId, index) => (
                <div key={`${powerUpId}-${index}`} className={styles.activePowerUp}>
                  {POWER_UPS[powerUpId]?.name}
                </div>
              ))}
            </div>
          )}
          
          {/* Chopping Area */}
          <div className={`${styles.sliderContainer} ${choppingAnimation ? styles.chopping : ''}`} onClick={handleChop}>
            <div className={styles.sliderTrack}>
              {/* Target zones */}
              {targetZones.map(zone => (
                <div
                  key={zone.id}
                  className={`${styles.targetZone} ${zone.moving ? styles.movingZone : ''}`}
                  style={{
                    left: `${zone.position}%`,
                    width: `${zone.size}%`,
                    backgroundColor: zone.color,
                    borderColor: zone.color,
                  }}
                />
              ))}
              
              {/* Power-up spawn */}
              {powerUpSpawn && (
                <div
                  className={styles.powerUpZone}
                  style={{
                    left: `${powerUpSpawn.position}%`,
                  }}
                >
                  ⚡
                </div>
              )}
              
              {/* Slider indicator */}
              <div
                className={`${styles.sliderIndicator} ${choppingAnimation ? styles.choppingIndicator : ''}`}
                style={{
                  left: `${sliderPosition}%`,
                  backgroundColor: isInTargetZone(sliderPosition) ? '#27ae60' : 
                                 isInPowerUp(sliderPosition) ? '#ffeb3b' : '#e74c3c',
                  transform: `scale(${isInTargetZone(sliderPosition) || isInPowerUp(sliderPosition) ? 1.3 : 1})`,
                  boxShadow: isInTargetZone(sliderPosition) ? '0 0 15px #27ae60' : 
                           isInPowerUp(sliderPosition) ? '0 0 15px #ffeb3b' : 'none',
                }}
              />
            </div>
            
            <div className={styles.sliderInstructions}>
              🪓 Click to chop when on target zones! ⚡ Hit power-ups for bonuses!<br/>
              💡 Build combos by hitting consecutive strikes within 2 seconds!
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.choppingOverlay}>
      <div className={styles.choppingWindow}>
        <h3 className={styles.title}>Enhanced Wood Chopping Challenge</h3>
        <p className={styles.messageArea}>{message}</p>
        {renderGameArea()}
        <div className={styles.actions}>
          {phase === CHOPPING_PHASES.RESULT && (
            <button 
              onClick={() => { 
                resetGameState(); 
                onGameEnd({ outcome: 'closed' }); 
              }} 
              className={styles.actionButton}
            >
              Close
            </button>
          )}
          {phase === CHOPPING_PHASES.ACTIVE && (
            <button 
              onClick={() => { 
                resetGameState(); 
                onGameEnd({ outcome: 'cancelled' }); 
              }} 
              className={`${styles.actionButton} ${styles.cancelButton}`}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WoodChoppingMinigame; 